import { inject, Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { firebaseAnalyticsConsentStorage } from '../../../Application/Provider/injection-token/consent-storage.it';
import { Consent } from '../../../Domain/Model/Consent/consent';
import { ConsentRepository, defaultConsent } from '../../../Domain/Port/consent.repository';

@Injectable({
    providedIn: 'root'
})
/**
 * Repository service for managing user consent persistence using permanent storage.
 * This service handles saving and retrieving consent preferences, ensuring they
 * persist across application sessions.
 */
export class PermanentStorageConsentRepository extends ConsentRepository {
    /**
     * BehaviorSubject that holds and emits the current consent state.
     * Initialized with default consent (not given, not prompted).
     */
    private consentSubject: BehaviorSubject<Consent> = new BehaviorSubject<Consent>(defaultConsent);

    /**
     * Permanent storage service injected for persisting consent data.
     */
    private readonly permanentStorage = inject(firebaseAnalyticsConsentStorage);

    /**
     * Initializes the consent repository.
     * Calls the init method to load any previously saved consent from permanent storage.
     * Handles initialization errors by logging them to the console.
     */
    constructor() {
        super();
        this.init().catch(error => {
            console.error('Failed to initialize consent:', error);
        });
    }

    /**
     * Gets the current consent state as an Observable.
     * This allows components to reactively respond to consent changes.
     *
     * @returns An Observable that emits the current consent state and any future changes
     */
    getConsent(): Observable<Consent> {
        return this.consentSubject.asObservable();
    }

    /**
     * Sets the consent state in permanent storage.
     * Updates the consentSubject with the new consent value.
     *
     * @param consent - The consent state to save
     * @returns A Promise that resolves when the consent is saved, or rejects on error
     * @throws Error if saving to permanent storage fails
     */
    async setConsent(consent: Consent): Promise<void> {
        try {
            await this.permanentStorage.setItem(consent);
            this.consentSubject.next(consent);
        } catch (error) {
            console.error('Failed to save consent to storage:', error);
            this.consentSubject.next(consent); // Optional: Optimistic update
            throw error; // Propagate error to caller
        }
    }

    /**
     * Initializes the consent repository by loading saved consent from permanent storage.
     * If saved consent exists, it updates the consentSubject with the saved value.
     * If an error occurs or no saved consent exists, it uses the default consent.
     *
     * @returns A Promise that resolves when initialization is complete
     */
    private async init(): Promise<void> {
        try {
            const savedConsent = await this.permanentStorage.getItem();
            if (savedConsent) {
                this.consentSubject.next(savedConsent);
            }
        } catch (error) {
            console.error('Failed to load consent from storage:', error);
            this.consentSubject.next(defaultConsent);
        }
    }
}
